import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useReveal } from '../../hooks/useReveal';
import { Send, CheckCircle, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t, isRTL } = useLanguage();
  const [ref, visible] = useReveal();
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', message: '' });
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', organization: '', message: '' });
    } catch { setStatus('error'); }
  };

  return (
    <section id="contact" ref={ref} className="py-24 bg-[#0A1628]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{t('contact.title')}</h2>
          <div className="w-16 h-1 bg-[#00B4D8] mx-auto mt-6 rounded-full" />
          <p className="text-base text-slate-400 mt-6 max-w-2xl mx-auto">{t('contact.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-5xl mx-auto">
          <div className={`lg:col-span-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '100ms' }}>
            {status === 'success' ? (
              <div className="bg-white/5 border border-white/10 rounded-xl p-10 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <p className="text-white font-medium">{t('contact.success')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input required placeholder={t('contact.name')} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:border-[#00B4D8] transition-colors" />
                  <input required type="email" placeholder={t('contact.email')} value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:border-[#00B4D8] transition-colors" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input placeholder={t('contact.org')} value={form.organization} onChange={e => setForm(p => ({ ...p, organization: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:border-[#00B4D8] transition-colors" />
                  <input placeholder={t('contact.phone')} value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:border-[#00B4D8] transition-colors" />
                </div>
                <textarea required rows={4} placeholder={t('contact.message')} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-slate-500 focus:border-[#00B4D8] transition-colors resize-none" />
                <button type="submit" disabled={status === 'sending'}
                  className="flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0094B3] text-white font-semibold px-7 py-3 rounded-lg transition-colors text-sm disabled:opacity-50">
                  <Send className="w-4 h-4" /> {t('contact.submit')}
                </button>
              </form>
            )}
          </div>

          <div className={`lg:col-span-2 space-y-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-[#00B4D8]" /></div>
              <div><p className="text-xs text-slate-500 mb-1">Email</p><a href="mailto:info@nexa.sa" className="text-white text-sm hover:text-[#00B4D8] transition-colors">info@nexa.sa</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-[#00B4D8]" /></div>
              <div><p className="text-xs text-slate-500 mb-1">Phone</p><a href="tel:+966533993007" className="text-white text-sm hover:text-[#00B4D8] transition-colors">+966-533-993-007</a></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#00B4D8]" /></div>
              <div><p className="text-xs text-slate-500 mb-1">Website</p><a href="https://www.nexa-sol.com" className="text-white text-sm hover:text-[#00B4D8] transition-colors">www.nexa-sol.com</a></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
